/**
 * Die Zelle des **Entwicklers**: wer die Revision übergeben hat — der
 * Anzeigename der Person, wenn ein Administrator ihre SVN-Kennung zugeordnet
 * hat, sonst die Kennung selbst.
 *
 * **An einer Stelle für beide Listen** (#129). Der Revisionsstrom und der
 * Befundvorrat zeigen dieselbe Auskunft; zwei Aufbauten wären zwei Antworten auf
 * dieselbe Frage, sobald einer nachgezogen wird und der andere nicht — dieselbe
 * Entscheidung wie bei `MessageCell`, `FindingCell` und `IssueReferenceCell`.
 *
 * **Die Kopfzeile heißt „Entwickler", der Schlüssel der Spalte bleibt `person`**
 * (ADR-0009; `CONTEXT.md`, „Person"). ADR-0009 trennt die Achsen: Der Bezeichner
 * folgt dem Glossarbegriff — dahinter steht die **Person**, aufgelöst über ihre
 * **SVN-Kennung** —, die Kopfzeile ist Oberflächentext und darf das konkretere
 * Wort tragen. „Person" ist im Glossar der Mensch im System überhaupt, auch das
 * `management`, das nie committet; was in dieser Spalte steht, ist der, der die
 * Revision übergeben hat, und dafür ist „Entwickler" das Wort aus dem Betrieb.
 *
 * **Eine Revision wird nie zurückgehalten, weil ihre Kennung noch niemandem
 * gehört.** Sie steht dann eben unter ihrer Kennung da, und der Titel sagt,
 * warum kein Name daneben steht.
 *
 * **Ohne Kennung bleibt die Zelle leer** — kein Strich, kein Platzhalter. Eine
 * Stelle aus einem Bestandslauf hat keinen Commit und damit keinen, der ihn
 * übergeben hat (Glossar, „Befunde an meinem Code"); eine erfundene Zuschreibung
 * wäre hier der teuerste Fehler.
 *
 * Name und Kennung stehen zusammen vollständig im `title`, weil die Spalte
 * schmal ist (`listWidths.ts`).
 */

import type { ReactElement } from "react";

/** Was an einer Kennung steht, die noch keiner Person gehört. */
const UNASSIGNED = "Diese SVN-Kennung ist noch keiner Person zugeordnet.";

export function PersonCell({
  svnIdentity,
  personName,
}: {
  svnIdentity: string;
  personName: string | null;
}): ReactElement | null {
  if (svnIdentity === "") {
    return null;
  }

  if (personName === null) {
    return <span title={UNASSIGNED}>{svnIdentity}</span>;
  }

  return (
    <span
      title={`${personName} · ${svnIdentity}`}
      style={{ display: "inline-flex", alignItems: "baseline", gap: "var(--sp-4)" }}
    >
      <span>{personName}</span>
      <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>{svnIdentity}</span>
    </span>
  );
}
